import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormHelperText,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Publish as PublishIcon,
  Lock as LockIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import config from '../config/config';
import SubscribersPanel from './SubscribersPanel';
import PlanPeriodCalendar from './PlanPeriodCalendar';
import './SubscriptionManagementPage.css';
import { parseLocalDate } from '../utils/dateFormatter';

// Use the shared config: a staging build only sets REACT_APP_ENV, so reading
// REACT_APP_API_BASE_URL directly here used to fall back to localhost.
const API_BASE_URL = config.API_BASE_URL;

// Sentinels for the team filter, prefixed so they can never collide with a real team name.
const TEAM_FILTER_ALL = '__all_plans__';
const TEAM_FILTER_UNRESTRICTED = '__open_to_all__';

/** A plan with no eligible teams — or the literal 'all' — is open to every team. */
const isOpenToAllTeams = (plan) =>
  !plan.eligibleTeams || plan.eligibleTeams.length === 0 || plan.eligibleTeams.includes('all');

const describeTeams = (plan) => (isOpenToAllTeams(plan) ? 'All Teams' : plan.eligibleTeams.join(', '));

/** Newest iteration first — iteration numbers climb, so the highest is the latest. */
const newestFirst = (list) =>
  [...list].sort((a, b) => String(b.periodKey || '').localeCompare(String(a.periodKey || '')));

/** Native <input type=month|week> emits exactly the period-key format the API expects. */
const periodInputType = (planType) => (planType === 'WEEKLY' ? 'week' : 'month');
const periodPlaceholder = (planType) => (planType === 'WEEKLY' ? '2026-W32' : '2026-09');

/** How many iterations an expanded plan shows before "show all". */
const ITERATION_PREVIEW_LIMIT = 3;

/**
 * Filtering by a team answers "what can this team subscribe to?", so plans open to every
 * team match as well — they are available to the selected team too.
 */
const matchesTeamFilter = (plan, filter) => {
  if (filter === TEAM_FILTER_ALL) return true;
  if (filter === TEAM_FILTER_UNRESTRICTED) return isOpenToAllTeams(plan);
  return isOpenToAllTeams(plan) || plan.eligibleTeams.includes(filter);
};

/** Plan ids out of a "1,4,7" URL parameter, ignoring anything that isn't one. */
const parseIdList = (raw) =>
  (raw || '')
    .split(',')
    .map((value) => Number(value.trim()))
    .filter((id) => Number.isInteger(id) && id > 0);

const SubscriptionManagementPage = () => {
  const { getAccessToken, hasFeature } = useAuth();
  const canViewSubscribers = hasFeature('CONTENT_SUBSCRIPTION_AUDIT');

  // The URL owns where you are on this page: which tab, which view, which plans are open and
  // which one you were pointed at. A refresh or a shared link lands in the same place instead
  // of bouncing back to the default tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get('tab');
    if (tab === 'plans') return 'plans';
    if (tab === 'subscribers' && canViewSubscribers) return 'subscribers';
    return canViewSubscribers ? 'subscribers' : 'plans';
  });

  const [plans, setPlans] = useState([]);
  const [archivedPlans, setArchivedPlans] = useState([]);
  const [planViewMode, setPlanViewMode] = useState(() => {
    const view = searchParams.get('view');
    if (view === 'list' || view === 'card') return view;
    // A link that opens plans can only mean the list view, where expanding exists.
    return parseIdList(searchParams.get('expanded')).length > 0 ? 'list' : 'card';
  });
  const [expandedPlanIds, setExpandedPlanIds] = useState(() => parseIdList(searchParams.get('expanded')));
  const [focusedPlanId, setFocusedPlanId] = useState(() => {
    const [id] = parseIdList(searchParams.get('plan'));
    return id || null;
  });
  const [planIterations, setPlanIterations] = useState({});
  const [planIterationsLoading, setPlanIterationsLoading] = useState({});
  const [showAllIterations, setShowAllIterations] = useState({});
  const [bundleDialog, setBundleDialog] = useState(null);
  const [planTeamFilter, setPlanTeamFilter] = useState(TEAM_FILTER_ALL);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Plan CRUD
  const [openDialog, setOpenDialog] = useState(false);
  // Save errors belong in the dialog: the page-level Alert renders behind the modal,
  // so a validation failure looked like nothing happened at all.
  const [formError, setFormError] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '', description: '', type: 'MONTHLY', price: '', createSquarePlan: true, eligibleTeams: ['all'],
    digitalOnlyLabel: '', firstBillingPeriod: '', booksRequired: false,
  });

  // Iterations
  const [openIterationsDialog, setOpenIterationsDialog] = useState(false);
  const [periodTiming, setPeriodTiming] = useState(null);
  const [calendarVersion, setCalendarVersion] = useState(0);
  // Set when Add was opened as "add content to this period" — that period already ships a book,
  // so offering a Book Bundle field would only lead to a 409.
  const [forceContentOnly, setForceContentOnly] = useState(false);
  const [periodIterations, setPeriodIterations] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [iterations, setIterations] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [bookBundles, setBookBundles] = useState([]);
  const [openAddIterationDialog, setOpenAddIterationDialog] = useState(false);
  const [editingIteration, setEditingIteration] = useState(null);
  const [iterationFormData, setIterationFormData] = useState({
    periodKey: '', bundleId: '', bookBundleId: '', status: 'DRAFT',
  });

  // Set by the server once anyone has paid for this period. A charged period is settled in
  // full — book, content, period and status alike.
  const booksFrozen = Boolean(editingIteration?.booksFrozen);
  const chargedSubscribers = editingIteration?.chargedSubscribers || 0;

  // Only one iteration per period may carry books, so an iteration can offer a Book Bundle
  // only when it is that one — or when the period has no book iteration yet and it could
  // become it. Everything else in the period is a content drop, on edit as much as on add:
  // the previous version only asked this while adding, so opening an existing content drop
  // offered a book that the server was always going to refuse.
  const bookIterationInPeriod = iterationFormData.periodKey
    ? iterations.find((it) => it.periodKey === iterationFormData.periodKey
        && it.bookBundleId != null)
    : null;
  const isPeriodsBookIteration = Boolean(editingIteration)
    && bookIterationInPeriod?.id === editingIteration.id;
  const contentOnly = forceContentOnly
    || (Boolean(bookIterationInPeriod) && !isPeriodsBookIteration);

  useEffect(() => {
    fetchPlans();
    fetchArchivedPlans();
    fetchBundles();
    fetchBookBundles();
    fetchTeams();
  }, []);

  const teamNames = useMemo(
    () => teams.map((t) => (typeof t === 'string' ? t : t.name)).filter(Boolean),
    [teams]
  );

  const visiblePlans = useMemo(
    () => plans.filter((plan) => matchesTeamFilter(plan, planTeamFilter)),
    [plans, planTeamFilter]
  );

  // Keep the URL in step with where the admin actually is, without stacking history entries.
  useEffect(() => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('tab', activeTab);
      if (activeTab === 'plans') {
        next.set('view', planViewMode);
        if (expandedPlanIds.length > 0) next.set('expanded', expandedPlanIds.join(','));
        else next.delete('expanded');
        if (focusedPlanId) next.set('plan', String(focusedPlanId));
        else next.delete('plan');
      } else {
        next.delete('view');
        next.delete('expanded');
        next.delete('plan');
      }
      return next;
    }, { replace: true });
  }, [activeTab, planViewMode, expandedPlanIds, focusedPlanId, setSearchParams]);

  // A link can arrive with plans already expanded, so load those iterations up front.
  useEffect(() => {
    expandedPlanIds.forEach((planId) => loadPlanIterations(planId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll the plan named in the URL into view once it has actually rendered.
  const scrolledToPlanRef = useRef(null);
  useEffect(() => {
    if (!focusedPlanId || scrolledToPlanRef.current === focusedPlanId) return;
    const element = document.getElementById(`plan-${focusedPlanId}`);
    if (!element) return;
    scrolledToPlanRef.current = focusedPlanId;
    // Scrolling is a nicety; the highlight already marks the plan. Never let it break the page.
    if (typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [focusedPlanId, visiblePlans, planViewMode, activeTab]);

  // --- Data Fetching ---

  const fetchArchivedPlans = async () => {
    try {
      const token = await getAccessToken();
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/content/subscription-plans/archived`,
        { headers: { Authorization: `Bearer ${token}` } });
      setArchivedPlans(response.data || []);
    } catch (err) {
      // The archive is a secondary view; failing to load it should not take the page with it.
      console.error('Error fetching archived plans:', err);
    }
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const token = await getAccessToken();
      const response = await axios.get(`${API_BASE_URL}/api/v1/content/subscription-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data || []);
      setError(null);
      return response.data || [];
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError('Failed to load subscription plans');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const fetchBundles = async () => {
    try {
      const token = await getAccessToken();
      const response = await axios.get(`${API_BASE_URL}/api/v1/content/bundles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBundles(response.data || []);
    } catch (err) {
      console.error('Error fetching bundles:', err);
    }
  };

  const fetchBookBundles = async () => {
    try {
      const token = await getAccessToken();
      const response = await axios.get(`${API_BASE_URL}/api/v1/content/admin/book-bundles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookBundles(response.data || []);
    } catch (err) {
      console.error('Error fetching book bundles:', err);
    }
  };

  const fetchTeams = async () => {
    try {
      const token = await getAccessToken();
      const response = await axios.get(`${API_BASE_URL}/em/v1/admin/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      if (data.status === 'success') {
        setTeams(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  /**
   * Timing for one period, straight from the calendar endpoint. Deriving it here would mean
   * duplicating ISO-week arithmetic and the billing lead window in the browser, where they
   * would drift from what actually charges people.
   */
  const loadPeriodTiming = async (planId, periodKey) => {
    try {
      const data = await loadPlanCalendar(planId, periodKey, periodKey);
      return data?.periods?.[0] || null;
    } catch (err) {
      console.error('Error loading period timing:', err);
      return null;
    }
  };

  const loadPlanCalendar = async (planId, from, to) => {
    const token = await getAccessToken();
    const response = await axios.get(
      `${API_BASE_URL}/api/v1/content/subscription-plans/${planId}/calendar`,
      { params: { from, to }, headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  };

  const fetchIterations = async (planId) => {
    try {
      const token = await getAccessToken();
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/content/subscription-plans/${planId}/iterations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIterations(response.data || []);
      // The list view renders its own copy of this plan's iterations, so refresh it here too —
      // otherwise confirming or deleting from the dialog leaves the expanded row stale.
      setPlanIterations((prev) => ({ ...prev, [planId]: response.data || [] }));
      // The calendar holds its own server-side copy — it counts billed subscribers and empty
      // periods, which this response does not carry — so it has to be told to refetch.
      setCalendarVersion((v) => v + 1);
    } catch (err) {
      console.error('Error fetching iterations:', err);
      setError('Failed to load iterations');
    }
  };

  /** Loads a plan's iterations for the list view once; repeat calls are no-ops. */
  const loadPlanIterations = async (planId) => {
    if (planIterations[planId]) return;
    setPlanIterationsLoading((prev) => ({ ...prev, [planId]: true }));
    try {
      const token = await getAccessToken();
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/content/subscription-plans/${planId}/iterations`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlanIterations((prev) => ({ ...prev, [planId]: response.data || [] }));
    } catch (err) {
      console.error('Error fetching iterations:', err);
      setError('Failed to load iterations');
      setExpandedPlanIds((prev) => prev.filter((id) => id !== planId));
    } finally {
      setPlanIterationsLoading((prev) => ({ ...prev, [planId]: false }));
    }
  };

  /** Expand/collapse a plan's iterations in the list view, loading them the first time. */
  const handleTogglePlanIterations = (plan) => {
    if (expandedPlanIds.includes(plan.id)) {
      setExpandedPlanIds((prev) => prev.filter((id) => id !== plan.id));
      return;
    }
    setExpandedPlanIds((prev) => [...prev, plan.id]);
    loadPlanIterations(plan.id);
  };

  /** Bundle details, fetched fresh — the list endpoints don't carry assets or items. */
  const handleOpenBundleDialog = async (kind, id, fallbackTitle) => {
    setBundleDialog({ kind, id, fallbackTitle, loading: true, data: null, error: null });
    const url = kind === 'content'
      ? `${API_BASE_URL}/api/v1/content/bundles/${id}`
      : `${API_BASE_URL}/api/v1/content/admin/book-bundles/${id}`;
    try {
      const token = await getAccessToken();
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setBundleDialog((prev) =>
        prev && prev.id === id && prev.kind === kind
          ? { ...prev, loading: false, data: response.data }
          : prev);
    } catch (err) {
      console.error('Error fetching bundle details:', err);
      setBundleDialog((prev) =>
        prev && prev.id === id && prev.kind === kind
          ? { ...prev, loading: false, error: 'Failed to load bundle details' }
          : prev);
    }
  };

  // --- Plan CRUD Handlers ---

  const handleOpenDialog = (plan = null) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name, description: plan.description || '', type: plan.type,
        price: plan.price.toString(), createSquarePlan: false,
        eligibleTeams: plan.eligibleTeams || ['all'],
        digitalOnlyLabel: plan.digitalOnlyLabel || '',
        firstBillingPeriod: plan.firstBillingPeriod || '',
        booksRequired: !!plan.booksRequired,
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: '', description: '', type: 'MONTHLY', price: '', createSquarePlan: true, eligibleTeams: ['all'],
        digitalOnlyLabel: '', firstBillingPeriod: '', booksRequired: false,
      });
    }
    setFormError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setFormError(null);
    setOpenDialog(false);
    setEditingPlan(null);
    setFormData({
      name: '', description: '', type: 'MONTHLY', price: '', createSquarePlan: true, eligibleTeams: ['all'],
      digitalOnlyLabel: '', firstBillingPeriod: '', booksRequired: false,
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleTeamSelection = (e) => {
    const { value, checked } = e.target;
    setFormData(prev => {
      const currentTeams = prev.eligibleTeams;
      if (value === 'all') {
        return { ...prev, eligibleTeams: checked ? ['all'] : [] };
      }
      const filteredTeams = currentTeams.filter(t => t !== 'all');
      const updatedTeams = checked
        ? [...filteredTeams, value]
        : filteredTeams.filter(t => t !== value);
      return { ...prev, eligibleTeams: updatedTeams };
    });
  };

  const handleSubmit = async () => {
    try {
      const token = await getAccessToken();
      const payload = {
        name: formData.name, description: formData.description,
        type: formData.type, price: parseFloat(formData.price),
        createSquarePlan: formData.createSquarePlan,
        eligibleTeams: formData.eligibleTeams || ['all'],
        // Blank rather than omitted: the backend reads blank as "clear it", which is how a
        // deferred plan is turned back into an immediate-start one.
        firstBillingPeriod: (formData.firstBillingPeriod || '').trim(),
        booksRequired: !!formData.booksRequired,
        digitalOnlyLabel: formData.digitalOnlyLabel && formData.digitalOnlyLabel.trim() !== ''
          ? formData.digitalOnlyLabel.trim()
          : null,
      };
      let createdPlan = null;
      if (editingPlan) {
        await axios.put(`${API_BASE_URL}/api/v1/content/subscription-plans/${editingPlan.id}`, payload,
          { headers: { Authorization: `Bearer ${token}` } });
        setSuccessMessage('Subscription plan updated successfully');
      } else {
        const response = await axios.post(`${API_BASE_URL}/api/v1/content/subscription-plans`, payload,
          { headers: { Authorization: `Bearer ${token}` } });
        createdPlan = response.data || null;
        setSuccessMessage('Subscription plan created as Inactive — switch it to Active when it is ready to sell');
      }
      handleCloseDialog();
      const refreshed = await fetchPlans();

      if (createdPlan?.id) {
        // The refetch is a second request, and a list that comes back without the plan we just
        // created is what forced a manual page refresh. The POST response is authoritative, so
        // fall back to it rather than trusting the reload.
        if (refreshed && !refreshed.some((plan) => plan.id === createdPlan.id)) {
          setPlans((prev) => [...prev, createdPlan]);
        }
        // Neither the tab nor a team filter should be able to hide the plan just created.
        if (!matchesTeamFilter(createdPlan, planTeamFilter)) setPlanTeamFilter(TEAM_FILTER_ALL);
        setActiveTab('plans');
        setFocusedPlanId(createdPlan.id);
      }
    } catch (err) {
      console.error('Error saving plan:', err);
      // `details` carries the per-field reason ("price: Price must be positive"); `message` is
      // only ever the generic "Validation failed", so prefer details when the backend sends it.
      const data = err.response?.data;
      const reason = (data?.details && String(data.details).replace(/;\s*$/, ''))
        || data?.message
        || 'Failed to save subscription plan';
      setFormError(reason);
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm(
      'Delete this subscription plan permanently, along with its iterations?\n\n' +
      'A plan anyone has ever subscribed to cannot be deleted — you will be offered the archive '
      + 'instead, which clears it off this list and keeps every record intact.'
    )) return;
    try {
      const token = await getAccessToken();
      await axios.delete(`${API_BASE_URL}/api/v1/content/subscription-plans/${planId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setSuccessMessage('Subscription plan deleted successfully');
      fetchPlans();
    } catch (err) {
      console.error('Error deleting plan:', err);
      const message = err.response?.data?.message || 'Failed to delete subscription plan';
      // The backend refuses a plan carrying history and says whether anyone is still on it.
      // When nobody is, the archive is the actual answer — so offer it here rather than
      // making the admin read an error, find another button and try again.
      const canArchive = err.response?.data?.errorCode === 'SUBSCRIPTION_PLAN_IN_USE'
        && message.includes('archived');
      if (canArchive && window.confirm(`${message}\n\nArchive it now?`)) {
        await archivePlan(planId);
        return;
      }
      setError(message);
    }
  };

  const archivePlan = async (planId) => {
    try {
      const token = await getAccessToken();
      await axios.post(`${API_BASE_URL}/api/v1/content/subscription-plans/${planId}/archive`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      setSuccessMessage('Plan archived. It is off the plans list and out of the member catalogue.');
      fetchPlans();
      fetchArchivedPlans();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to archive the plan');
    }
  };

  const handleArchive = async (plan) => {
    if (!window.confirm(
      `Archive "${plan.name}"?\n\n`
      + 'It comes off this list and stops being offered to members. Every subscription record on '
      + 'it stays exactly as it is, and you can bring it back from the Archived tab.'
    )) return;
    await archivePlan(plan.id);
  };

  const handleUnarchive = async (plan) => {
    try {
      const token = await getAccessToken();
      await axios.post(`${API_BASE_URL}/api/v1/content/subscription-plans/${plan.id}/unarchive`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      // Restored to the list, not to the catalogue — switching it back on is a separate decision.
      setSuccessMessage(`"${plan.name}" is back on the Plans list, still inactive.`);
      fetchPlans();
      fetchArchivedPlans();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to restore the plan');
    }
  };

  const handleToggleActive = async (plan) => {
    // Deactivating only stops the plan being offered — nobody's live subscription is cancelled,
    // and they keep billing on it. Say so, since the two are easy to confuse.
    if (plan.isActive && !window.confirm(
      `Stop offering "${plan.name}" to members?\n\n` +
      'It disappears from the plans members can subscribe to. Existing subscriptions on it are ' +
      'NOT cancelled — they keep running and billing as normal.'
    )) return;
    try {
      const token = await getAccessToken();
      // The API field is isActive — sending `active` was silently ignored, which is why the
      // toggle appeared dead. Send only the flag so nothing else on the plan can be clobbered.
      await axios.put(`${API_BASE_URL}/api/v1/content/subscription-plans/${plan.id}`,
        { isActive: !plan.isActive },
        { headers: { Authorization: `Bearer ${token}` } });
      setSuccessMessage(`Subscription plan ${!plan.isActive ? 'activated' : 'deactivated'} successfully`);
      fetchPlans();
    } catch (err) {
      console.error('Error toggling plan status:', err);
      setError(err.response?.data?.message || 'Failed to update subscription plan status');
    }
  };

  // --- Iteration Handlers ---

  const handleOpenIterationsDialog = async (plan) => {
    setSelectedPlan(plan);
    await fetchIterations(plan.id);
    setOpenIterationsDialog(true);
  };

  const handleCloseIterationsDialog = () => {
    setOpenIterationsDialog(false);
    setSelectedPlan(null);
    setIterations([]);
  };

  const handleOpenAddIterationDialog = (iteration = null, presetPeriodKey = null,
                                       contentOnly = false) => {
    setForceContentOnly(contentOnly);
    if (iteration) {
      setEditingIteration(iteration);
      setIterationFormData({
        periodKey: iteration.periodKey || '',
        bundleId: iteration.bundleId || '',
        bookBundleId: iteration.bookBundleId != null ? iteration.bookBundleId : '',
        status: iteration.status || 'DRAFT',
      });
    } else {
      setEditingIteration(null);
      // Clicking an empty calendar box seeds the period, so the admin does not retype it.
      setIterationFormData({
        periodKey: presetPeriodKey || '', bundleId: '', bookBundleId: '', status: 'DRAFT',
      });
    }
    setOpenAddIterationDialog(true);
  };

  const handleCloseAddIterationDialog = () => {
    setOpenAddIterationDialog(false);
    setEditingIteration(null);
    setIterationFormData({ periodKey: '', bundleId: '', bookBundleId: '', status: 'DRAFT' });
    setForceContentOnly(false);
  };

  // Confirming after the cut-off means the cycle was already priced without this bucket, so
  // its members were billed for content only. Nothing here blocks that — a late confirmation
  // still settles up — but the admin should see the clock before it runs out.
  useEffect(() => {
    const planId = selectedPlan?.id;
    const periodKey = iterationFormData.periodKey;
    if (!openAddIterationDialog || !planId || !periodKey) {
      setPeriodTiming(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const timing = await loadPeriodTiming(planId, periodKey);
      if (!cancelled) setPeriodTiming(timing);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openAddIterationDialog, selectedPlan?.id, iterationFormData.periodKey]);

  const handleIterationInputChange = (e) => {
    const { name, value } = e.target;
    setIterationFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleReleaseNow = async (iteration) => {
    if (!window.confirm(
      `Release ${iteration.periodKey} content now?\n\n`
      + 'Every active subscriber gets it immediately, whether or not they have been billed for '
      + 'this period yet. This cannot be undone.\n\n'
      + 'Books are not affected — they ship when they are paid for.'
    )) return;
    try {
      const token = await getAccessToken();
      await axios.post(
        `${API_BASE_URL}/api/v1/content/subscription-plans/${selectedPlan.id}/iterations/${iteration.id}/release-now`,
        {}, { headers: { Authorization: `Bearer ${token}` } });
      setSuccessMessage('Content released to all active subscribers');
      await fetchIterations(selectedPlan.id);
    } catch (err) {
      console.error('Error releasing content:', err);
      setError(err.response?.data?.message || 'Failed to release the content');
    }
  };

  const handleSubmitIteration = async () => {
    try {
      const token = await getAccessToken();
      const payload = {
        periodKey: iterationFormData.periodKey,
        bundleId: parseInt(iterationFormData.bundleId),
        bookBundleId: !contentOnly && iterationFormData.bookBundleId !== ''
          && iterationFormData.bookBundleId != null
          ? parseInt(iterationFormData.bookBundleId, 10)
          : null,
        status: iterationFormData.status || 'DRAFT',
      };
      if (editingIteration) {
        await axios.put(
          `${API_BASE_URL}/api/v1/content/subscription-plans/${selectedPlan.id}/iterations/${editingIteration.id}`,
          payload, { headers: { Authorization: `Bearer ${token}` } });
        setSuccessMessage('Iteration updated successfully');
      } else {
        await axios.post(
          `${API_BASE_URL}/api/v1/content/subscription-plans/${selectedPlan.id}/iterations`,
          payload, { headers: { Authorization: `Bearer ${token}` } });
        setSuccessMessage('Iteration added successfully');
      }
      handleCloseAddIterationDialog();
      await fetchIterations(selectedPlan.id);
      fetchPlans();  // the plan row shows an iteration count, so it moved too
    } catch (err) {
      console.error('Error saving iteration:', err);
      setError(err.response?.data?.message || 'Failed to save iteration');
    }
  };

  const handleDeleteIteration = async (iterationId) => {
    if (!window.confirm('Are you sure you want to delete this iteration?')) return;
    try {
      const token = await getAccessToken();
      await axios.delete(
        `${API_BASE_URL}/api/v1/content/subscription-plans/${selectedPlan.id}/iterations/${iterationId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      setSuccessMessage('Iteration deleted successfully');
      handleCloseAddIterationDialog();
      await fetchIterations(selectedPlan.id);
      fetchPlans();  // keep the plan row's iteration count honest
    } catch (err) {
      console.error('Error deleting iteration:', err);
      setError(err.response?.data?.message || 'Failed to delete iteration');
    }
  };

  // --- Render ---

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="subscription-management-container">
      <Box className="page-header">
        <Typography variant="h4" component="h1">
          Subscription Management
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>{successMessage}</Alert>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        Subscription book shipments are managed in{' '}
        <a href="/book-order-management" style={{ color: 'inherit', fontWeight: 600 }}>Book Orders</a>.
      </Alert>

      <Tabs
        value={activeTab}
        onChange={(e, value) => setActiveTab(value)}
        sx={{ mb: 3, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        {canViewSubscribers && <Tab label="Subscribers" value="subscribers" />}
        <Tab label="Plans" value="plans" />
        <Tab
          label={archivedPlans.length ? `Archived (${archivedPlans.length})` : 'Archived'}
          value="archived"
        />
      </Tabs>

      {/* ========================= Subscribers ========================= */}
      {activeTab === 'subscribers' && canViewSubscribers && (
        <SubscribersPanel plans={plans} teams={teams} />
      )}

      {/* ========================== Archived ========================== */}
      {activeTab === 'archived' && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Plans that are finished with. A plan anyone has ever subscribed to can never be
            deleted — that would take it out of those members&apos; records — so this is where such
            a plan goes once nobody is on it. Restoring one brings it back to the Plans list
            without putting it back in front of members.
          </Typography>

          {archivedPlans.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
              <Typography color="text.secondary">Nothing archived.</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Billing</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Periods</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {archivedPlans.map((plan) => (
                    <TableRow key={plan.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{plan.name}</Typography>
                        {plan.description && (
                          <Typography variant="caption" color="text.secondary">
                            {plan.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{plan.type === 'WEEKLY' ? 'Weekly' : 'Monthly'}</TableCell>
                      <TableCell>${Number(plan.price || 0).toFixed(2)}</TableCell>
                      <TableCell>{plan.iterationCount ?? 0}</TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="outlined"
                          onClick={() => handleUnarchive(plan)}
                          sx={{ textTransform: 'none' }}>
                          Restore
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* ======================== Plan Management ======================== */}
      {activeTab === 'plans' && (
      <>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-start"
            justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 240 }}>
                <InputLabel id="plan-team-filter-label">Team</InputLabel>
                <Select
                  labelId="plan-team-filter-label"
                  label="Team"
                  value={planTeamFilter}
                  onChange={(e) => setPlanTeamFilter(e.target.value)}
                >
                  <MenuItem value={TEAM_FILTER_ALL}>All teams</MenuItem>
                  <MenuItem value={TEAM_FILTER_UNRESTRICTED}>Open to all teams</MenuItem>
                  {teamNames.length > 0 && <Divider />}
                  {teamNames.map((name) => (
                    <MenuItem key={name} value={name}>{name}</MenuItem>
                  ))}
                </Select>
                {planTeamFilter !== TEAM_FILTER_ALL && planTeamFilter !== TEAM_FILTER_UNRESTRICTED && (
                  <FormHelperText>Plans open to all teams are included</FormHelperText>
                )}
              </FormControl>
              {planTeamFilter !== TEAM_FILTER_ALL && (
                <Typography variant="body2" color="textSecondary">
                  Showing {visiblePlans.length} of {plans.length}
                </Typography>
              )}
            </Box>

            <Box display="flex" alignItems="center" gap={1.5}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={planViewMode}
                onChange={(e, mode) => mode && setPlanViewMode(mode)}
                aria-label="Plan view"
              >
                <ToggleButton value="card" aria-label="Card view">
                  <Tooltip title="Card view" slotProps={{ transition: { timeout: 0 } }}>
                    <ViewModuleIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="list" aria-label="List view">
                  <Tooltip title="List view" slotProps={{ transition: { timeout: 0 } }}>
                    <ViewListIcon fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
              <Button variant="contained" color="primary" startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}>
                Create New Plan
              </Button>
            </Box>
          </Box>

          {visiblePlans.length === 0 ? (
            <Card>
              <CardContent>
                <Typography variant="body1" color="textSecondary" align="center">
                  {plans.length === 0
                    ? 'No subscription plans found. Create your first plan to get started.'
                    : 'No plans are available to this team.'}
                </Typography>
              </CardContent>
            </Card>
          ) : planViewMode === 'card' ? (
            <Grid container spacing={3}>
              {visiblePlans.map((plan) => (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={plan.id}>
                  <Card className="plan-card" id={`plan-${plan.id}`}
                    sx={focusedPlanId === plan.id
                      ? { outline: '2px solid', outlineColor: 'primary.main' }
                      : undefined}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="start" gap={1} mb={2}>
                        <Typography variant="h6" component="h2" className="plan-card-title">{plan.name}</Typography>
                        <Chip label={plan.isActive ? 'Active' : 'Inactive'}
                          color={plan.isActive ? 'success' : 'default'} size="small" />
                      </Box>
                      <Typography variant="body2" color="textSecondary" className="plan-card-description">
                        {plan.description || 'No description'}
                      </Typography>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" color="textSecondary">Type:</Typography>
                        <Chip label={plan.type} size="small" />
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" color="textSecondary">Base Price:</Typography>
                        <Typography variant="h6" color="primary">${plan.price?.toFixed(2)}</Typography>
                      </Box>
                      {plan.firstBillingPeriod && (
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body2" color="textSecondary">Starts:</Typography>
                          <Chip
                            label={plan.firstBillingPeriod}
                            color="info"
                            size="small"
                            variant="outlined"
                            title="Members subscribing before this period pay nothing until it starts"
                          />
                        </Box>
                      )}
                      {plan.booksRequired && (
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body2" color="textSecondary">Books:</Typography>
                          <Chip
                            label="Required"
                            color="secondary"
                            size="small"
                            variant="outlined"
                            title="Members must choose at least one book set to subscribe"
                          />
                        </Box>
                      )}
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2" color="textSecondary">Square:</Typography>
                        <Chip
                          label={plan.squarePlanId ? 'Connected' : 'Not connected'}
                          color={plan.squarePlanId ? 'success' : 'default'}
                          size="small" variant="outlined"
                        />
                      </Box>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
                        <Typography variant="body2" color="textSecondary" sx={{ flexShrink: 0 }}>Teams:</Typography>
                        <Typography variant="body2" className="plan-card-teams" title={describeTeams(plan)}>
                          {describeTeams(plan)}
                        </Typography>
                      </Box>
                    </CardContent>
                    <CardActions>
                      <Tooltip title="Manage Iterations" slotProps={{ transition: { timeout: 0 } }}>
                        <IconButton size="small" color="primary" aria-label="Manage iterations" onClick={() => handleOpenIterationsDialog(plan)}>
                          <ScheduleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Plan" slotProps={{ transition: { timeout: 0 } }}>
                        <IconButton size="small" color="primary" onClick={() => handleOpenDialog(plan)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Archive — takes it off this list, keeps every record"
                        slotProps={{ transition: { timeout: 0 } }}>
                        <IconButton size="small" onClick={() => handleArchive(plan)}>
                          <ArchiveIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Plan" slotProps={{ transition: { timeout: 0 } }}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(plan.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <Box flexGrow={1} />
                      <FormControlLabel
                        control={<Switch checked={!!plan.isActive} onChange={() => handleToggleActive(plan)} color="primary" />}
                        label={plan.isActive ? 'Active' : 'Inactive'}
                      />
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell>Plan</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Base Price</TableCell>
                    <TableCell>Teams</TableCell>
                    <TableCell>Square</TableCell>
                    <TableCell align="right">Iterations</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visiblePlans.map((plan) => {
                    const isExpanded = expandedPlanIds.includes(plan.id);
                    const ordered = newestFirst(planIterations[plan.id] || []);
                    const showingAll = !!showAllIterations[plan.id];
                    const shown = showingAll ? ordered : ordered.slice(0, ITERATION_PREVIEW_LIMIT);
                    return (
                    <React.Fragment key={plan.id}>
                    <TableRow hover id={`plan-${plan.id}`} selected={focusedPlanId === plan.id}>
                      <TableCell sx={{ width: 48 }}>
                        <Tooltip title={isExpanded ? 'Hide iterations' : 'Show iterations'}
                          slotProps={{ transition: { timeout: 0 } }}>
                          <IconButton size="small" aria-expanded={isExpanded}
                            aria-label={isExpanded ? 'Hide iterations' : 'Show iterations'}
                            onClick={() => handleTogglePlanIterations(plan)}>
                            <ChevronRightIcon fontSize="small" sx={{
                              transition: 'transform 0.15s',
                              transform: isExpanded ? 'rotate(90deg)' : 'none',
                            }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="body2" fontWeight={600}>{plan.name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {plan.description || 'No description'}
                        </Typography>
                      </TableCell>
                      <TableCell><Chip label={plan.type} size="small" /></TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="primary" fontWeight={600}>
                          ${plan.price?.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="body2" title={describeTeams(plan)} noWrap>
                          {describeTeams(plan)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={plan.squarePlanId ? 'Connected' : 'Not connected'}
                          color={plan.squarePlanId ? 'success' : 'default'}
                          size="small" variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">{plan.iterationCount ?? 0}</TableCell>
                      <TableCell>
                        <FormControlLabel
                          sx={{ mr: 0 }}
                          control={<Switch size="small" checked={!!plan.isActive}
                            onChange={() => handleToggleActive(plan)} color="primary" />}
                          label={<Typography variant="body2">{plan.isActive ? 'Active' : 'Inactive'}</Typography>}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" justifyContent="flex-end">
                          <Tooltip title="Manage Iterations" slotProps={{ transition: { timeout: 0 } }}>
                            <IconButton size="small" color="primary" aria-label="Manage iterations" onClick={() => handleOpenIterationsDialog(plan)}>
                              <ScheduleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Plan" slotProps={{ transition: { timeout: 0 } }}>
                            <IconButton size="small" color="primary" onClick={() => handleOpenDialog(plan)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Archive — takes it off this list, keeps every record"
                            slotProps={{ transition: { timeout: 0 } }}>
                            <IconButton size="small" onClick={() => handleArchive(plan)}>
                              <ArchiveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Plan" slotProps={{ transition: { timeout: 0 } }}>
                            <IconButton size="small" color="error" onClick={() => handleDelete(plan.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={9} className="plan-iterations-cell"
                        sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{
                            my: 1.5, ml: 2, pl: 2,
                            borderLeft: '3px solid', borderColor: 'primary.light',
                          }}>
                            {planIterationsLoading[plan.id] ? (
                              <Box display="flex" alignItems="center" gap={1} py={1}>
                                <CircularProgress size={18} />
                                <Typography variant="body2" color="textSecondary">Loading iterations…</Typography>
                              </Box>
                            ) : ordered.length === 0 ? (
                              <Typography variant="body2" color="textSecondary" sx={{ py: 1 }}>
                                No iterations yet — use Manage Iterations to add the first one.
                              </Typography>
                            ) : (
                              <>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Iteration</TableCell>
                                      <TableCell>Period</TableCell>
                                      <TableCell>Status</TableCell>
                                      <TableCell>Content Bundle</TableCell>
                                      <TableCell>Book Bundle</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {shown.map((iteration) => {
                                      const bookBundle = iteration.bookBundleId != null
                                        ? bookBundles.find((bb) => bb.id === iteration.bookBundleId)
                                        : null;
                                      return (
                                        <TableRow key={iteration.id}>
                                          <TableCell>
                                            <Box display="flex" alignItems="center" gap={1}>
                                              <Typography variant="body2" fontWeight={600}>
                                                {iteration.periodKey || '—'}
                                              </Typography>
                                              {iteration.id === ordered[0].id && (
                                                <Chip label="Latest" size="small" color="info" variant="outlined" />
                                              )}
                                            </Box>
                                          </TableCell>
                                          <TableCell>
                                            {iteration.periodStart
                                              ? parseLocalDate(iteration.periodStart).toLocaleDateString()
                                              : 'Not set'}
                                          </TableCell>
                                          <TableCell>
                                            <Chip
                                              label={iteration.status === 'CONFIRMED' ? 'Confirmed' : 'Draft'}
                                              color={iteration.status === 'CONFIRMED' ? 'success' : 'default'}
                                              variant={iteration.status === 'CONFIRMED' ? 'filled' : 'outlined'}
                                              size="small"
                                            />
                                          </TableCell>
                                          <TableCell>
                                            {iteration.bundleId != null ? (
                                              <Link component="button" type="button" variant="body2" underline="hover"
                                                onClick={() => handleOpenBundleDialog(
                                                  'content', iteration.bundleId, iteration.bundleTitle)}>
                                                {iteration.bundleTitle || `Bundle #${iteration.bundleId}`}
                                              </Link>
                                            ) : (
                                              <Typography variant="body2" color="textSecondary">
                                                No bundle assigned
                                              </Typography>
                                            )}
                                          </TableCell>
                                          <TableCell>
                                            {iteration.bookBundleId != null ? (
                                              <Link component="button" type="button" variant="body2" underline="hover"
                                                onClick={() => handleOpenBundleDialog(
                                                  'book', iteration.bookBundleId, bookBundle?.title)}>
                                                {bookBundle ? bookBundle.title : `Bundle #${iteration.bookBundleId}`}
                                              </Link>
                                            ) : (
                                              <Typography variant="body2" color="textSecondary">Digital only</Typography>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>

                                {ordered.length > ITERATION_PREVIEW_LIMIT && (
                                  <Box mt={1}>
                                    <Button size="small" onClick={() => setShowAllIterations((prev) => ({
                                      ...prev, [plan.id]: !showingAll,
                                    }))}>
                                      {showingAll
                                        ? `Show latest ${ITERATION_PREVIEW_LIMIT} only`
                                        : `Show all ${ordered.length} iterations`}
                                    </Button>
                                  </Box>
                                )}
                              </>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                    </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
      </>
      )}

      {/* ======================== DIALOGS ======================== */}

      {/* Create/Edit Plan Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPlan ? 'Edit Subscription Plan' : 'Create New Subscription Plan'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {formError && (
              <Alert severity="error" onClose={() => setFormError(null)} sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}
            <TextField fullWidth label="Plan Name" name="name" value={formData.name}
              onChange={handleInputChange} margin="normal" required />
            <TextField fullWidth label="Description" name="description" value={formData.description}
              onChange={handleInputChange} margin="normal" multiline rows={3} />
            <FormControl fullWidth margin="normal">
              <InputLabel>Type</InputLabel>
              <Select name="type" value={formData.type} onChange={handleInputChange} label="Type">
                <MenuItem value="WEEKLY">Weekly</MenuItem>
                <MenuItem value="MONTHLY">Monthly</MenuItem>
              </Select>
            </FormControl>
            <TextField fullWidth label="Base Price" name="price" type="number" value={formData.price}
              onChange={handleInputChange} margin="normal" required inputProps={{ min: 0, step: 0.01 }} />

            <TextField
              fullWidth
              label="Default option label"
              name="digitalOnlyLabel"
              value={formData.digitalOnlyLabel}
              onChange={handleInputChange}
              margin="normal"
              placeholder="Digital only"
              helperText='Shown to users as the default choice when no shipping option is selected. Leave blank to use "Digital only".'
              inputProps={{ maxLength: 100 }}
            />

            <TextField
              fullWidth
              label="First billing period (optional)"
              name="firstBillingPeriod"
              type={periodInputType(formData.type)}
              value={formData.firstBillingPeriod}
              onChange={handleInputChange}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              helperText={`Members who subscribe before this period pay nothing until it starts — their card is saved and first charged on the 1st. Leave blank to charge on signup. Format ${periodPlaceholder(formData.type)}.`}
            />

            <FormControlLabel
              sx={{ mt: 1, alignItems: 'flex-start' }}
              control={
                <Switch
                  name="booksRequired"
                  checked={!!formData.booksRequired}
                  onChange={handleInputChange}
                  color="primary"
                />
              }
              label={
                <span>
                  Require a book bundle
                  <Typography variant="caption" display="block" color="text.secondary">
                    Members must choose at least one set to subscribe, so a shipping address and
                    variable-billing consent become mandatory. A month with no book bundle still
                    bills content-only. Existing subscribers on 0 sets are not changed.
                  </Typography>
                </span>
              }
            />

            <Divider sx={{ my: 2 }} />

            <div style={{ marginBottom: '16px', marginTop: '16px' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px' }}>Eligible Teams</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <input type="checkbox" value="all" checked={formData.eligibleTeams.includes('all')} onChange={handleTeamSelection} />
                  All Teams
                </label>
                {teams.map(team => (
                  <label key={team.id || team.name} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input type="checkbox" value={team.name} checked={formData.eligibleTeams.includes(team.name)} onChange={handleTeamSelection} disabled={formData.eligibleTeams.includes('all')} />
                    {team.name}
                  </label>
                ))}
              </div>
            </div>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary"
            disabled={!formData.name || !formData.price}>
            {editingPlan ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bundle details, opened from an iteration row in the list view. The list endpoints
          don't carry assets or items, so this fetches the bundle fresh. */}
      <Dialog open={!!bundleDialog} onClose={() => setBundleDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {bundleDialog?.data?.title || bundleDialog?.fallbackTitle || 'Bundle'}
          <Typography variant="caption" display="block" color="textSecondary">
            {bundleDialog?.kind === 'book' ? 'Book bundle' : 'Content bundle'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {bundleDialog?.loading && (
            <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>
          )}
          {bundleDialog?.error && <Alert severity="error">{bundleDialog.error}</Alert>}
          {bundleDialog?.data && (
            <>
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                <Chip size="small" color="primary"
                  label={`$${Number(bundleDialog.data.price ?? 0).toFixed(2)}`} />
                <Chip size="small" variant="outlined"
                  color={bundleDialog.data.isActive ? 'success' : 'default'}
                  label={bundleDialog.data.isActive ? 'Active' : 'Inactive'} />
                {bundleDialog.data.type && (
                  <Chip size="small" variant="outlined" label={bundleDialog.data.type} />
                )}
                {bundleDialog.data.releaseDate && (
                  <Chip size="small" variant="outlined"
                    label={`Released ${new Date(bundleDialog.data.releaseDate).toLocaleDateString()}`} />
                )}
              </Box>

              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {bundleDialog.data.description || 'No description'}
              </Typography>

              <Divider sx={{ my: 2 }} />

              {bundleDialog.kind === 'content' ? (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Content ({bundleDialog.data.assets?.length ?? bundleDialog.data.assetCount ?? 0})
                  </Typography>
                  {!bundleDialog.data.assets || bundleDialog.data.assets.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      Nothing in this bundle yet.
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableBody>
                        {bundleDialog.data.assets.map((asset) => (
                          <TableRow key={asset.id}>
                            <TableCell>{asset.title}</TableCell>
                            <TableCell align="right">
                              <Chip size="small" variant="outlined" label={asset.type} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </>
              ) : (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Books ({bundleDialog.data.items?.length ?? 0})
                  </Typography>
                  {!bundleDialog.data.items || bundleDialog.data.items.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No books in this bundle yet.
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableBody>
                        {bundleDialog.data.items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.bookTitle || `Book #${item.bookId}`}</TableCell>
                            <TableCell align="right">× {item.quantity ?? 1}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" color="textSecondary">
                Teams: {describeTeams(bundleDialog.data)}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setBundleDialog(null)}>Close</Button></DialogActions>
      </Dialog>

      {/* Iterations Dialog */}
      <Dialog open={openIterationsDialog} onClose={handleCloseIterationsDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Manage Iterations - {selectedPlan?.name}</Typography>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Button variant="contained" color="primary" startIcon={<AddIcon />}
                onClick={() => handleOpenAddIterationDialog()}>
                Add Iteration
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <PlanPeriodCalendar
              plan={selectedPlan}
              loadCalendar={loadPlanCalendar}
              refreshKey={calendarVersion}
              onAddForPeriod={(periodKey) => handleOpenAddIterationDialog(null, periodKey)}
              onOpenIteration={(period) => {
                const inPeriod = iterations.filter((it) => it.periodKey === period.periodKey);
                if (inPeriod.length > 1) {
                  setPeriodIterations(period.periodKey);
                  return;
                }
                if (inPeriod.length === 1) handleOpenAddIterationDialog(inPeriod[0]);
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={handleCloseIterationsDialog}>Close</Button></DialogActions>
      </Dialog>

      {/* One period's iterations. A month ships one parcel and can release several content
          drops, so this is the list that shows both and lets either be opened. */}
      <Dialog open={Boolean(periodIterations)} onClose={() => setPeriodIterations(null)}
        maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{periodIterations}</Typography>
            <Button size="small" variant="outlined" startIcon={<AddIcon />}
              onClick={() => { handleOpenAddIterationDialog(null, periodIterations, true);
                               setPeriodIterations(null); }}>
              Add content
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <List dense>
            {iterations.filter((it) => it.periodKey === periodIterations).map((it) => {
              const bookBundle = it.bookBundleId != null
                ? bookBundles.find((bb) => bb.id === it.bookBundleId) : null;
              return (
                <ListItem key={it.id} divider
                  secondaryAction={
                    <Tooltip title="Edit" slotProps={{ transition: { timeout: 0 } }}>
                      <IconButton edge="end" color="primary"
                        onClick={() => { handleOpenAddIterationDialog(it);
                                         setPeriodIterations(null); }}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  }>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight={600}>
                          {it.bundleTitle || `Bundle #${it.bundleId}`}
                        </Typography>
                        <Chip size="small"
                          label={it.status === 'CONFIRMED' ? 'Confirmed' : 'Draft'}
                          color={it.status === 'CONFIRMED' ? 'success' : 'default'}
                          variant={it.status === 'CONFIRMED' ? 'filled' : 'outlined'} />
                        {it.booksFrozen && (
                          <Tooltip title={`Paid for by ${it.chargedSubscribers} subscriber${it.chargedSubscribers === 1 ? '' : 's'}`}
                            slotProps={{ transition: { timeout: 0 } }}>
                            <LockIcon fontSize="inherit" color="error" />
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={bookBundle
                      ? `Ships ${bookBundle.title}`
                      : it.bookBundleId != null
                        ? `Ships bundle #${it.bookBundleId}`
                        : 'Content only — nothing ships'} />
                </ListItem>
              );
            })}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPeriodIterations(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Iteration Dialog */}
      <Dialog open={openAddIterationDialog} onClose={handleCloseAddIterationDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingIteration ? 'Edit Iteration' : 'Add New Iteration'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {booksFrozen && (
              <Alert severity="info" icon={<LockIcon fontSize="small" />} sx={{ mb: 1 }}>
                <strong>
                  {chargedSubscribers} subscriber{chargedSubscribers === 1 ? ' has' : 's have'}
                  {' '}been charged for {editingIteration?.periodKey}.
                </strong>{' '}
                Everything here is settled — it is what they paid for, and the shipping was
                quoted from it. A period can hold any number of content drops though, and only
                one of them carries books, so more content goes in a new iteration.
                <Box mt={1}>
                  {/* Pre-filled with this period and no book: the one thing that is still
                      allowed, one click away from the message that says so. */}
                  <Button size="small" variant="outlined" startIcon={<AddIcon />}
                    onClick={() => handleOpenAddIterationDialog(
                      null, editingIteration?.periodKey, true)}>
                    Add content for {editingIteration?.periodKey}
                  </Button>
                </Box>
              </Alert>
            )}
            <TextField fullWidth label="Period" name="periodKey"
              type={periodInputType(selectedPlan?.type)}
              value={iterationFormData.periodKey} onChange={handleIterationInputChange}
              margin="normal" required InputLabelProps={{ shrink: true }}
              disabled={booksFrozen}
              placeholder={periodPlaceholder(selectedPlan?.type)}
              helperText={selectedPlan?.type === 'WEEKLY'
                ? 'The week this bucket covers. One bucket per week, and it is what that week\'s billing is priced from.'
                : 'The month this bucket covers. One bucket per month, and it is what that month\'s billing is priced from.'} />
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Content Bundle</InputLabel>
              <Select name="bundleId" value={iterationFormData.bundleId}
                onChange={handleIterationInputChange} label="Content Bundle"
                disabled={booksFrozen}>
                <MenuItem value=""><em>Select a bundle</em></MenuItem>
                {bundles.map((b) => <MenuItem key={b.id} value={b.id}>{b.title}</MenuItem>)}
              </Select>
            </FormControl>
            {contentOnly ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>Content only.</strong> {iterationFormData.periodKey} already ships
                {' '}{bookBundles.find((bb) => bb.id === bookIterationInPeriod?.bookBundleId)?.title
                  || `bundle #${bookIterationInPeriod?.bookBundleId}`} on another iteration, and a
                period has just one parcel to price and post — so this one adds content to the
                month without changing what is shipped or charged.
              </Alert>
            ) : (
              <FormControl fullWidth margin="normal">
                <InputLabel>Book Bundle</InputLabel>
                <Select name="bookBundleId" value={iterationFormData.bookBundleId}
                  onChange={handleIterationInputChange} label="Book Bundle"
                  disabled={booksFrozen}>
                  <MenuItem value=""><em>None (digital-only)</em></MenuItem>
                  {bookBundles.map((bb) => (
                    <MenuItem key={bb.id} value={bb.id}>{`${bb.title} — $${Number(bb.price || 0).toFixed(2)}`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl fullWidth margin="normal" required>
              <InputLabel>Status</InputLabel>
              <Select name="status" value={iterationFormData.status}
                onChange={handleIterationInputChange} label="Status">
                {/* A paid-for period cannot go back to draft: every resolver looks for
                    CONFIRMED, so it would vanish from billing and from unlocking. */}
                <MenuItem value="DRAFT" disabled={booksFrozen}>
                  Draft — still being put together
                </MenuItem>
                <MenuItem value="CONFIRMED">Confirmed — this is the period's content</MenuItem>
              </Select>
              <FormHelperText>
                Confirming settles what this period ships, and is what its billing is priced from.
                Subscribers see the content when their payment for the period clears.
              </FormHelperText>
            </FormControl>
            {periodTiming && (
              <Alert
                severity={periodTiming.cutoffPassed ? 'warning'
                  : periodTiming.daysUntilCutoff <= 2 ? 'warning' : 'info'}
                sx={{ mt: 1 }}>
                {periodTiming.cutoffPassed ? (
                  <>
                    {periodTiming.periodKey} was priced on
                    {' '}{new Date(periodTiming.cutoffAt).toLocaleDateString()}, so members billed
                    since then paid for content only. Confirming now still releases the content to
                    them, and any book here is charged separately.
                  </>
                ) : (
                  <>
                    <strong>
                      {periodTiming.daysUntilCutoff} day{periodTiming.daysUntilCutoff === 1 ? '' : 's'}
                      {' '}left to confirm {periodTiming.periodKey}.
                    </strong>
                    {' '}Billing is priced on {new Date(periodTiming.cutoffAt).toLocaleDateString()};
                    after that, this period's book is not part of that charge.
                  </>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
          {/* These act on the period itself rather than on the form, so they sit apart from
              Cancel/Save. Publish Now needs confirmed content to release. */}
          <Box>
            {editingIteration && (
              <>
                <Tooltip title={booksFrozen
                  ? 'Subscribers have paid for this period — it cannot be removed'
                  : ''} slotProps={{ transition: { timeout: 0 } }}>
                  <span>
                    <Button size="small" color="error" startIcon={<DeleteIcon />}
                      disabled={booksFrozen}
                      onClick={() => handleDeleteIteration(editingIteration.id)}>
                      Delete
                    </Button>
                  </span>
                </Tooltip>
                {editingIteration.status === 'CONFIRMED' && (
                  <Button size="small" color="warning" startIcon={<PublishIcon />}
                    onClick={() => handleReleaseNow(editingIteration)}>
                    Publish now
                  </Button>
                )}
              </>
            )}
          </Box>
          <Box>
            <Button onClick={handleCloseAddIterationDialog}>Cancel</Button>
            <Button onClick={handleSubmitIteration} variant="contained" color="primary"
              disabled={!iterationFormData.periodKey || !iterationFormData.bundleId}>
              {editingIteration ? 'Update' : 'Add'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionManagementPage;
